// Compute sizing engine — PRODUCT-50.
// Cardinal rule: headroom is applied ONCE.
// vcpuOvercommitRatio and platformOverheadPct are separate independent factors — never combine.

import type { VMConfig, SizingConfig, ComputeResult, Environment, HostSpec, Application } from '../../types';

// Standard reference host specification for enterprise virtualisation clusters.
// Typical dual-socket 2U rack server (AMD EPYC 9004 / Intel Xeon Sapphire Rapids).
const REFERENCE_HOST_PHYSICAL_CORES = 48; // 2 sockets × 24 cores
const REFERENCE_HOST_MEMORY_GB = 512;
const REFERENCE_HOST_MIN_CPU_GHZ = 2.0;

function ceiling(value: number): number {
  return Math.ceil(value);
}

function vmAppliesToSite(vm: VMConfig, site: 'primary' | 'dr'): boolean {
  return vm.siteAssignment === site || vm.siteAssignment === 'both';
}

/**
 * Look up tier name from applications list.
 */
function findTierName(applications: Application[], tierId: string): string | undefined {
  for (const app of applications) {
    const tier = app.tiers.find(t => t.id === tierId);
    if (tier) return tier.name;
  }
  return undefined;
}

/**
 * Run the compute sizing engine for a given site.
 * Returns one ComputeResult per tier per environment that has VMs at this site.
 *
 * @param vms - All VMs to size
 * @param config - Sizing configuration
 * @param site - 'primary' or 'dr'
 * @param applications - Application list for tier name lookup (optional for backward compat)
 */
export function runComputeSizing(
  vms: VMConfig[],
  config: SizingConfig,
  site: 'primary' | 'dr',
  applications: Application[] = [],
): ComputeResult[] {
  const siteVms = vms.filter(vm => vmAppliesToSite(vm, site));
  if (siteVms.length === 0) return [];

  const results: ComputeResult[] = [];

  // Group by tier first
  const tierGroups = new Map<string, VMConfig[]>();
  for (const vm of siteVms) {
    const key = vm.tierId;
    if (!tierGroups.has(key)) tierGroups.set(key, []);
    tierGroups.get(key)!.push(vm);
  }

  for (const [tierId, tierVms] of tierGroups) {
    // Then group by environment within each tier
    const environments = [...new Set(tierVms.map(vm => vm.environment))] as Environment[];

    for (const env of environments) {
      const envVms = tierVms.filter(vm => vm.environment === env);
      const result = computeForSiteEnv(envVms, config, site, env);
      const tierName = findTierName(applications, tierId);

      results.push({
        ...result,
        tierId,
        tierName,
      });
    }
  }

  return results;
}

function computeForSiteEnv(
  vms: VMConfig[],
  config: SizingConfig,
  site: 'primary' | 'dr',
  environment: Environment,
): ComputeResult {
  const notes: string[] = [];

  // Apply environment-specific overrides when present
  const envOverride = config.environmentOverrides?.[environment];
  const vcpuOvercommitRatio = envOverride?.vcpuOvercommitRatio ?? config.vcpuOvercommitRatio;
  const memoryOvercommitRatio = envOverride?.memoryOvercommitRatio ?? config.memoryOvercommitRatio;

  // Step 1: Sum raw vCPU and memory
  const rawVcpuRequired = vms.reduce((sum, vm) => sum + vm.vcpu * vm.quantity, 0);
  const rawMemoryGbRequired = vms.reduce((sum, vm) => sum + vm.memoryGb * vm.quantity, 0);
  const totalVmCount = vms.reduce((sum, vm) => sum + vm.quantity, 0);

  notes.push(
    `Raw vCPU required: ${rawVcpuRequired} (${totalVmCount} VM instance(s) across ${vms.length} type(s))`,
  );
  notes.push(`Raw memory required: ${rawMemoryGbRequired} GB`);

  // Cold DR: no compute provisioned at DR site — storage replication continues independently
  if (site === 'dr' && config.drTier === 'cold') {
    notes.push(
      `DR tier is Cold — no compute provisioned at DR site. ` +
      `Storage replication proceeds independently per DR storage rules.`,
    );
    return {
      site,
      environment,
      rawVcpuRequired,
      rawMemoryGbRequired,
      physicalCoresRequired: 0,
      physicalMemoryGbRequired: 0,
      hostCountWithoutHA: 0,
      hostCountWithHA: 0,
      recommendedHostSpec: {
        physicalCores: REFERENCE_HOST_PHYSICAL_CORES,
        memoryGb: REFERENCE_HOST_MEMORY_GB,
        minCpuGhz: REFERENCE_HOST_MIN_CPU_GHZ,
      },
      hasUltraLowLatencyVms: false,
      latencyConstraintCount: 0,
      notes,
    };
  }

  // Step 2: Apply DR compute fraction for Warm / Hot DR site
  let effectiveVcpu = rawVcpuRequired;
  let effectiveMemGb = rawMemoryGbRequired;

  if (site === 'dr') {
    const fraction = config.drComputeFractionPct;
    effectiveVcpu = rawVcpuRequired * fraction;
    effectiveMemGb = rawMemoryGbRequired * fraction;
    notes.push(
      `DR site (${config.drTier}): DR compute fraction ${fraction * 100}% applied → ` +
      `effective vCPU: ${effectiveVcpu}, effective RAM: ${effectiveMemGb} GB`,
    );
  }

  // Step 3: Physical cores — vCPU overcommit (headroom factor 1 of 2)
  const physCores = ceiling(effectiveVcpu / vcpuOvercommitRatio);
  notes.push(
    `Physical cores after vCPU overcommit ` +
    `(${effectiveVcpu} ÷ ${vcpuOvercommitRatio}): ${physCores}`,
  );

  // Step 4: Platform overhead applied independently — NEVER combined with overcommit ratio
  const physCoresWithOverhead = ceiling(physCores * (1 + config.platformOverheadPct));
  notes.push(
    `Physical cores with platform overhead ` +
    `(${physCores} × ${1 + config.platformOverheadPct}): ${physCoresWithOverhead}`,
  );

  // Step 5: Compute-driven host count
  const hostCountComputeDriven = ceiling(physCoresWithOverhead / REFERENCE_HOST_PHYSICAL_CORES);
  notes.push(
    `Compute-driven host count ` +
    `(${physCoresWithOverhead} cores ÷ ${REFERENCE_HOST_PHYSICAL_CORES} cores/host): ${hostCountComputeDriven}`,
  );

  // Step 6: Physical memory — overcommit then overhead (same two independent factors)
  const physicalMemoryGbRequired = ceiling(
    (effectiveMemGb / memoryOvercommitRatio) * (1 + config.platformOverheadPct),
  );
  notes.push(
    `Physical memory: (${effectiveMemGb} GB ÷ ${memoryOvercommitRatio}) × ` +
    `${1 + config.platformOverheadPct} = ${physicalMemoryGbRequired} GB`,
  );

  // Step 7: Memory-driven host count
  const hostCountMemoryDriven = ceiling(physicalMemoryGbRequired / REFERENCE_HOST_MEMORY_GB);
  notes.push(
    `Memory-driven host count ` +
    `(${physicalMemoryGbRequired} GB ÷ ${REFERENCE_HOST_MEMORY_GB} GB/host): ${hostCountMemoryDriven}`,
  );

  // Step 8: Binding constraint — whichever drives the higher host count wins
  const hostCountWithoutHA = Math.max(hostCountComputeDriven, hostCountMemoryDriven);
  if (hostCountMemoryDriven > hostCountComputeDriven) {
    notes.push(
      `Memory is the binding constraint ` +
      `(memory: ${hostCountMemoryDriven} hosts > compute: ${hostCountComputeDriven} hosts)`,
    );
  } else {
    notes.push(
      `Compute is the binding constraint ` +
      `(compute: ${hostCountComputeDriven} hosts ≥ memory: ${hostCountMemoryDriven} hosts)`,
    );
  }

  // Step 9: Apply HA model
  const haSpares = config.haModel === 'n_plus_1' ? 1 : 2;
  const hostCountWithHA = hostCountWithoutHA + haSpares;
  notes.push(
    `HA model ${config.haModel}: +${haSpares} spare host(s) → ` +
    `${hostCountWithoutHA} + ${haSpares} = ${hostCountWithHA} hosts total`,
  );

  // Step 10: Ultra-low latency check — reduces effective cluster capacity, never silently absorbed
  const ultraLowLatencyVms = vms.filter(
    vm => vm.networkConfig.latencySensitivity === 'ultra_low_latency',
  );
  const hasUltraLowLatencyVms = ultraLowLatencyVms.length > 0;
  const latencyConstraintCount = ultraLowLatencyVms.length;

  if (hasUltraLowLatencyVms) {
    const vmNames = ultraLowLatencyVms.map(vm => vm.name).join(', ');
    notes.push(
      `WARNING: ${latencyConstraintCount} VM type(s) require ultra-low latency (${vmNames}). ` +
      `These VMs require dedicated host co-location or NUMA pinning, which reduces effective ` +
      `cluster capacity. Review anti-affinity placement rules before finalising host count.`,
    );
  }

  // Step 11: Minimum CPU clock speed — take the highest requirement across all VMs
  const minCpuGhz = Math.max(
    REFERENCE_HOST_MIN_CPU_GHZ,
    ...vms.map(vm => vm.cpuSpeedMinGhz),
  );

  const recommendedHostSpec: HostSpec = {
    physicalCores: REFERENCE_HOST_PHYSICAL_CORES,
    memoryGb: REFERENCE_HOST_MEMORY_GB,
    minCpuGhz,
  };

  notes.push(
    `Recommended host spec: ≥${REFERENCE_HOST_PHYSICAL_CORES} physical cores, ` +
    `≥${REFERENCE_HOST_MEMORY_GB} GB RAM, ≥${minCpuGhz} GHz`,
  );

  return {
    site,
    environment,
    rawVcpuRequired,
    rawMemoryGbRequired,
    physicalCoresRequired: physCoresWithOverhead,
    physicalMemoryGbRequired,
    hostCountWithoutHA,
    hostCountWithHA,
    recommendedHostSpec,
    hasUltraLowLatencyVms,
    latencyConstraintCount,
    notes,
  };
}
